import React, { useState } from "react";
import {
  FiActivity,
  FiChevronDown,
  FiCheckCircle,
  FiExternalLink,
  FiFileText,
  FiHeart,
  FiHelpCircle,
  FiLock,
  FiMail,
  FiPhone,
  FiShield,
} from "react-icons/fi";
import ScreenHeader from "./ScreenHeader";

const PAZCARE_DASHBOARD_URL = "https://app.pazcare.com/dashboard/admin-overview";
const PAZCARE_CONTACT_EMAIL = "deeksha.pk@pazcare.com";
const PAZCARE_CC_EMAIL = "mayankarya@endresol.com";
const PAZCARE_CONTACT_PHONE = "7758065133";
const BENEFITS_PENDING_EMPLOYEE_EMAILS = new Set(["rishita.bohra@antrazal.com"]);
const BENEFITS_PENDING_MESSAGE = "Available after permanent employment confirmation";

const hasPendingBenefitsEligibility = (viewedUser = {}) =>
  BENEFITS_PENDING_EMPLOYEE_EMAILS.has(
    String(viewedUser?.email || viewedUser?.workEmail || "")
      .trim()
      .toLowerCase()
  );

const insuranceHighlights = [
  { label: "Insurance cover", value: "Rs. 5,00,000", detail: "Eligible hospitalization claims" },
  { label: "Family policy", value: "Parents, children, spouse", detail: "Keep enrolled member data updated" },
  { label: "Claim route", value: "Cashless", detail: "Use Pazcare and network hospitals" },
];

const cashlessSteps = [
  {
    title: "Before admission",
    detail:
      "Download the Pazcare app or open the dashboard, verify enrolled family members, and keep the policy e-card ready.",
  },
  {
    title: "At the hospital",
    detail:
      "Use a network hospital where possible. Carry the Pazcare e-card and government ID, then ask the insurance desk to start cashless pre-authorization.",
  },
  {
    title: "After approval",
    detail:
      "Pay only non-medical, excluded, or policy-shortfall amounts directly to the hospital. Raise emergencies in Pazcare as soon as possible.",
  },
];

const wellnessClaimSteps = [
  "Choose an eligible fitness or wellness service from a valid provider.",
  "Keep the invoice, payment proof, and any supporting document that explains the expense.",
  "Submit the bill through HRM Expenses under wellness reimbursement.",
  "Claims are reimbursed subject to the Rs. 12,000 yearly limit, eligibility, and documentation checks.",
];

const wellnessHighlights = [
  { label: "Annual allowance", value: "Rs. 12,000 / year", detail: "Fitness and wellness reimbursement" },
  { label: "Claim through", value: "HRM Expenses", detail: "Submit bills under wellness reimbursement" },
  { label: "Requirement", value: "Policy acknowledgement", detail: "Benefit unlocks after acknowledgement" },
];

const FITNESS_POLICY_MATCHERS = ["fitness", "wellness", "reimbursement"];

const isFitnessPolicy = (policy = {}) => {
  const haystack = [
    policy.title,
    policy.policyNumber,
    policy.category,
    policy.summary,
    policy.body,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return FITNESS_POLICY_MATCHERS.every((term) => haystack.includes(term));
};

const isPolicyActiveForBenefit = (policy = {}) => {
  const status = String(policy.status || "").trim().toLowerCase();
  if (!status) {
    return true;
  }
  return !["draft", "inactive", "archived", "rejected", "expired", "disabled"].some((blockedStatus) =>
    status.includes(blockedStatus)
  );
};

const getFitnessPolicyState = (policies = []) => {
  const fitnessPolicy = policies.find(isFitnessPolicy) || null;
  const isEnabled = Boolean(fitnessPolicy?.acknowledged && isPolicyActiveForBenefit(fitnessPolicy));
  return {
    fitnessPolicy,
    isEnabled,
  };
};

const BenefitCard = ({
  id,
  tone,
  icon,
  eyebrow,
  title,
  summary,
  amountLabel,
  amount,
  amountDetail,
  expanded,
  disabled = false,
  onToggle,
  children,
}) => (
  <section
    className={`new-hrm-benefits-card new-hrm-benefits-card--${tone} ${expanded ? "new-hrm-benefits-card--open" : ""} ${disabled ? "new-hrm-benefits-card--disabled" : ""}`}
    id={`${id}-benefit`}
  >
    <button
      aria-controls={`${id}-details`}
      aria-expanded={disabled ? false : expanded}
      className="new-hrm-benefits-card__trigger"
      disabled={disabled}
      onClick={onToggle}
      type="button"
    >
      <span className="new-hrm-benefits-card__icon">{icon}</span>
      <span className="new-hrm-benefits-card__copy">
        <small>{eyebrow}</small>
        <strong>{title}</strong>
        <span>{summary}</span>
      </span>
      <span className="new-hrm-benefits-card__amount">
        <strong>{amount}</strong>
        <small>{amountLabel}</small>
        <span>{amountDetail}</span>
      </span>
      <span className="new-hrm-benefits-card__action">
        {disabled ? (
          <FiLock aria-hidden="true" />
        ) : (
          <FiChevronDown className={expanded ? "new-hrm-benefits-card__chevron--open" : ""} aria-hidden="true" />
        )}
      </span>
    </button>
    <div
      className="new-hrm-benefits-card__details"
      hidden={disabled || !expanded}
      id={`${id}-details`}
    >
      {children}
    </div>
  </section>
);

const BenefitsScreen = ({ onAction, policies = [], viewedUser = {} }) => {
  const [expandedCards, setExpandedCards] = useState(() => new Set());
  const benefitsPending = hasPendingBenefitsEligibility(viewedUser);
  const { fitnessPolicy, isEnabled: fitnessBenefitEnabled } = getFitnessPolicyState(policies);
  const wellnessLockMessage = fitnessPolicy
    ? `Acknowledge ${fitnessPolicy.title} in Policies to unlock this reimbursement.`
    : "Fitness reimbursement is locked until the Fitness and Wellness Reimbursement policy is available and acknowledged.";
  const toggleBenefitCard = (cardId) => {
    if (benefitsPending) {
      return;
    }
    setExpandedCards((current) => {
      const next = new Set(current);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };

  const openBenefitCard = (cardId) => {
    if (benefitsPending) {
      return;
    }
    setExpandedCards((current) => new Set([...current, cardId]));
  };

  return (
    <section className="new-hrm-screen new-hrm-benefits-screen new-hrm-benefits-screen--reference">
      <ScreenHeader
        eyebrow="Rewards"
        title="Benefits"
        description={
          benefitsPending
            ? "Preview the benefits planned for you after permanent employment confirmation."
            : "Health cover and wellbeing support you can use now."
        }
        actions={
          <button className="new-hrm-secondary-button" type="button" onClick={() => onAction("hr")}>
            <FiHelpCircle aria-hidden="true" />
            <span>Ask HR</span>
          </button>
        }
      />

      <section className="new-hrm-benefits-overview" aria-labelledby="benefits-overview-title">
        <div className="new-hrm-benefits-overview__copy">
          <span className="new-hrm-reference-kicker">
            {benefitsPending ? "Your upcoming benefits" : "Your 2026 benefit experience"}
          </span>
          <h2 id="benefits-overview-title">
            {benefitsPending ? "Your benefits are visible and ready for the next stage." : "Health and wellbeing, clearly in one place."}
          </h2>
          <p>
            {benefitsPending
              ? "Review what will become available when the People Team confirms your permanent employment status."
              : "See what is active, understand how to use it, and take the next step without searching through policy documents."}
          </p>
          {benefitsPending ? (
            <div className="new-hrm-benefits-eligibility" role="status">
              <FiLock aria-hidden="true" />
              <span>
                <strong>{BENEFITS_PENDING_MESSAGE}</strong>
                Claims and provider access are currently disabled. Ask HR if you need clarification.
              </span>
            </div>
          ) : null}
          <div className="new-hrm-benefits-overview__actions">
            <button
              className="new-hrm-primary-button"
              disabled={benefitsPending}
              type="button"
              onClick={() => openBenefitCard("insurance")}
            >
              <FiShield aria-hidden="true" />
              <span>Review health cover</span>
            </button>
            {benefitsPending ? (
              <button
                className="new-hrm-secondary-button new-hrm-benefits-pazcare-link"
                disabled
                type="button"
              >
                <span>Open Pazcare</span>
                <FiLock aria-hidden="true" />
              </button>
            ) : (
              <a
                className="new-hrm-secondary-button new-hrm-benefits-pazcare-link"
                href={PAZCARE_DASHBOARD_URL}
                target="_blank"
                rel="noreferrer"
              >
                <span>Open Pazcare</span>
                <FiExternalLink aria-hidden="true" />
              </a>
            )}
          </div>
        </div>

        <aside className="new-hrm-benefits-overview__summary" aria-label="Current benefit summary">
          <img src="/assets/hrm/benefits-team.webp" alt="Antrazal colleagues working together" />
          <div>
            <span><small>Family health cover</small><strong>Rs. 5L</strong></span>
            <span><small>Wellness allowance</small><strong>Rs. 12K</strong></span>
            <span><small>Claim support</small><strong>Pazcare</strong></span>
          </div>
        </aside>
      </section>

      <section
        className="new-hrm-panel new-hrm-benefits-cards"
        aria-label={benefitsPending ? "Upcoming benefits" : "Available benefits"}
      >
        <header className="new-hrm-reference-section-heading">
          <div>
            <h2>{benefitsPending ? "Benefits planned for you" : "Your benefits at a glance"}</h2>
            <p>
              {benefitsPending
                ? "Benefit details remain visible while access is pending confirmation."
                : "Open a benefit for coverage, eligibility, and the exact next action."}
            </p>
          </div>
          <span>{benefitsPending ? "2 upcoming" : "2 available"}</span>
        </header>

        <div className="new-hrm-benefits-card-grid">
          <BenefitCard
          amount="Rs. 5,00,000"
          amountDetail={benefitsPending ? BENEFITS_PENDING_MESSAGE : "Cashless claim support through Pazcare"}
          amountLabel="Coverage limit"
          disabled={benefitsPending}
          eyebrow={benefitsPending ? "Upcoming group health insurance" : "Active group health insurance"}
          expanded={!benefitsPending && expandedCards.has("insurance")}
          icon={<FiShield aria-hidden="true" />}
          id="insurance"
          onToggle={() => toggleBenefitCard("insurance")}
          summary="Company-sponsored protection for eligible hospitalization expenses, with enrolled family support."
          title="Group Health Insurance"
          tone="insurance"
        >
          <dl className="new-hrm-benefits-facts">
            {insuranceHighlights.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
                <small>{item.detail}</small>
              </div>
            ))}
          </dl>

          <div className="new-hrm-benefits-detail-flow">
            <section className="new-hrm-benefits-detail-block new-hrm-benefits-process">
              <header>
                <h2>How to use the insurance</h2>
                <p>Follow these steps before a planned admission or during an emergency.</p>
              </header>
              <ol>
                {cashlessSteps.map((step, index) => (
                  <li key={step.title}>
                    <span>{index + 1}</span>
                    <p>
                      <strong>{step.title}</strong>
                      {step.detail}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="new-hrm-benefits-support" aria-label="Insurance service and support">
              <article className="new-hrm-benefits-detail-block new-hrm-benefits-pazcare">
                <span className="new-hrm-benefits-icon">
                  <FiHeart aria-hidden="true" />
                </span>
                <h2>Use Pazcare</h2>
                <p>
                  Download the Pazcare app from the Play Store or App Store, or use the dashboard to view policy cards,
                  check enrolled members, download the e-card, find hospitals, and start a claim.
                </p>
                <a href={PAZCARE_DASHBOARD_URL} target="_blank" rel="noreferrer">
                  Open Pazcare dashboard <FiExternalLink aria-hidden="true" />
                </a>
              </article>

              <article className="new-hrm-benefits-detail-block new-hrm-benefits-contact">
                <span className="new-hrm-benefits-icon new-hrm-benefits-icon--red">
                  <FiCheckCircle aria-hidden="true" />
                </span>
                <h2>Need data corrected?</h2>
                <p>
                  Contact Deeksha P at Pazcare if member data, link access, or policy details look incorrect. Always CC
                  Mayank on email.
                </p>
                <div>
                  <a href={`mailto:${PAZCARE_CONTACT_EMAIL}?cc=${PAZCARE_CC_EMAIL}`}>
                    <FiMail aria-hidden="true" /> {PAZCARE_CONTACT_EMAIL}
                  </a>
                  <a href={`tel:${PAZCARE_CONTACT_PHONE}`}>
                    <FiPhone aria-hidden="true" /> {PAZCARE_CONTACT_PHONE}
                  </a>
                  <span>CC: {PAZCARE_CC_EMAIL}</span>
                </div>
              </article>
            </section>
          </div>
          </BenefitCard>

          <BenefitCard
          amount="Rs. 12,000 / year"
          amountDetail={
            benefitsPending
              ? BENEFITS_PENDING_MESSAGE
              : fitnessBenefitEnabled
                ? "Ready for eligible claims"
                : "Ready to activate after policy review"
          }
          amountLabel="Annual reimbursement"
          disabled={benefitsPending}
          eyebrow={benefitsPending ? "Upcoming fitness and wellness reimbursement" : "Fitness and wellness reimbursement"}
          expanded={!benefitsPending && expandedCards.has("fitness")}
          icon={<FiActivity aria-hidden="true" />}
          id="fitness"
          onToggle={() => toggleBenefitCard("fitness")}
          summary="A yearly allowance that helps turn healthy habits into reimbursable routines."
          title="Fitness and Wellness"
          tone="wellness"
        >
          <dl className="new-hrm-benefits-facts">
            {wellnessHighlights.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
                <small>{item.detail}</small>
              </div>
            ))}
          </dl>

          <div className="new-hrm-benefits-detail-flow">
            <section className="new-hrm-benefits-detail-block">
              <h2>Build a routine that pays you back</h2>
              <p>
                Your annual wellness allowance supports eligible fitness, preventive wellness, and healthy routine
                expenses so your health habits support your work, energy, and long-term performance.
              </p>
              <ul>
                {wellnessClaimSteps.map((step) => (
                  <li key={step}>
                    <FiCheckCircle aria-hidden="true" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="new-hrm-benefits-wellness__claim-card" aria-label="Fitness wellness reimbursement amount">
              <small>Benefit status</small>
              <strong>{fitnessBenefitEnabled ? "Enabled" : "Ready to activate"}</strong>
              <span>
                {fitnessBenefitEnabled
                  ? "Submit bills and claim reimbursement through HRM Expenses."
                  : wellnessLockMessage}
              </span>
              <em className={fitnessBenefitEnabled ? "new-hrm-benefits-wellness__status new-hrm-benefits-wellness__status--ready" : "new-hrm-benefits-wellness__status"}>
                {fitnessBenefitEnabled ? "Policy acknowledged - benefit enabled" : "Policy acknowledgement required"}
              </em>
              <button
                className={fitnessBenefitEnabled ? "new-hrm-primary-button" : "new-hrm-secondary-button"}
                disabled={benefitsPending}
                type="button"
                onClick={() => onAction(fitnessBenefitEnabled ? "expense" : "policies")}
              >
                <FiFileText aria-hidden="true" /> {fitnessBenefitEnabled ? "Start wellness claim" : "Review policy"}
              </button>
            </section>
          </div>
          </BenefitCard>
        </div>
      </section>
    </section>
  );
};

export default BenefitsScreen;
